import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackView } from "../lib/api";
import SEO from "../components/SEO";
import {
  Layout, CalendarCheck, ShoppingBag, Package, Globe, Smartphone,
  Sparkles, Palette, FileText, Megaphone, Image as ImageIcon,
  Croissant, Building2, Scissors, Flower2, Dumbbell, Hammer, Store, ArrowRight, Check
} from "lucide-react";

const blocks = [
  {
    title: "Création de sites internet",
    eyebrow: "01",
    items: [
      { icon: Layout, name: "Site vitrine professionnel", desc: "Présentation claire et premium de votre activité." },
      { icon: CalendarCheck, name: "Site avec prise de rendez-vous", desc: "Calendrier intégré, notifications, paiement d'acompte optionnel." },
      { icon: ShoppingBag, name: "Site Click & Collect", desc: "Catalogue, panier, paiement, retrait en boutique." },
      { icon: Package, name: "C&C + Prise de RDV", desc: "Solution hybride pour activités mixtes." },
      { icon: Globe, name: "Refonte de site", desc: "Modernisation complète d'un site existant." },
      { icon: Smartphone, name: "Optimisation mobile", desc: "Version mobile parfaite, performante et accessible." },
    ],
  },
  {
    title: "Identité visuelle",
    eyebrow: "02",
    items: [
      { icon: Sparkles, name: "Création de logo", desc: "Plusieurs propositions, déclinaisons, fichiers sources." },
      { icon: Palette, name: "Charte graphique", desc: "Couleurs, typographies, règles d'usage." },
      { icon: FileText, name: "Cartes de visite", desc: "Conception premium, prêtes à imprimer." },
      { icon: Megaphone, name: "Flyers & affiches", desc: "Communication imprimée percutante." },
      { icon: ImageIcon, name: "Bannières réseaux sociaux", desc: "Optimisées Instagram, LinkedIn, Facebook." },
      { icon: Package, name: "Pack identité complet", desc: "Logo + charte + supports clé en main." },
    ],
  },
];

const industries = [
  { icon: Croissant, name: "Boulangerie / Pâtisserie", desc: "Vitrine + Click & Collect + commandes spéciales" },
  { icon: Building2, name: "Agence immobilière", desc: "Annonces dynamiques + prise de RDV + CRM léger" },
  { icon: Scissors, name: "Barber / Coiffeur", desc: "Réservation 24/7 + gestion des prestations" },
  { icon: Flower2, name: "Fleuriste", desc: "Boutique en ligne + livraison locale" },
  { icon: Dumbbell, name: "Salle de sport", desc: "Cours, abonnements, planning intégré" },
  { icon: Hammer, name: "Artisan", desc: "Portfolio + demande de devis intelligente" },
  { icon: Store, name: "Commerce local", desc: "Vitrine + Click & Collect simple" },
];

export default function Services() {
  useEffect(() => { trackView("/services"); }, []);
  return (
    <div className="pt-32 pb-24 bg-[#09090b] text-white min-h-screen">
      <SEO
        title="Services — Création de sites internet & identité visuelle"
        description="Sites vitrines, Click & Collect, prise de rendez-vous, refontes, logos et chartes graphiques. Solutions métier prêtes à l'emploi pour boulangeries, salons, immobilier, fleuristes et plus."
        keywords="services agence web, création site vitrine, click collect, refonte site, création logo, charte graphique, identité visuelle entreprise"
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Services</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl mb-6" data-testid="services-title">
          Tout ce dont votre entreprise a <span className="italic font-light">besoin</span>.
        </h1>
        <p className="text-zinc-400 max-w-2xl font-light leading-relaxed mb-20">
          Des sites premium, des identités visuelles fortes et des solutions métier prêtes à
          l'emploi. Une console de gestion dédiée pour chaque solution.
        </p>

        {blocks.map((block, bi) => (
          <section key={block.title} className="mb-24" data-testid={`block-${bi}`}>
            <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
              <div>
                <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-3">{block.eyebrow}</div>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight">{block.title}</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
              {block.items.map((it, i) => (
                <div key={it.name} className="bg-[#0a0a0c] p-8 card-lift hover:bg-[#101013]">
                  <it.icon size={26} className="text-[#d4af37] mb-5" strokeWidth={1.5} />
                  <h3 className="font-display text-xl mb-3">{it.name}</h3>
                  <p className="text-zinc-400 text-sm font-light leading-relaxed">{it.desc}</p>
                </div>
              ))}
            </div>
          </section>
        ))}

        <section className="mb-24" data-testid="services-industries">
          <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-3">03</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-12">Solutions métier</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {industries.map((it) => (
              <div key={it.name} className="bg-[#0a0a0c] p-8 card-lift">
                <it.icon size={26} className="text-[#d4af37] mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-xl mb-2">{it.name}</h3>
                <p className="text-zinc-500 text-sm">{it.desc}</p>
                <ul className="mt-4 space-y-1 text-xs text-zinc-400">
                  <li className="flex gap-2"><Check size={12} className="text-[#d4af37] mt-0.5" /> Console de gestion dédiée</li>
                  <li className="flex gap-2"><Check size={12} className="text-[#d4af37] mt-0.5" /> SEO local optimisé</li>
                </ul>
              </div>
            ))}
          </div>
        </section>

        <div className="border border-white/10 p-12 text-center">
          <h3 className="font-display text-3xl mb-4">Un projet en tête ?</h3>
          <p className="text-zinc-400 mb-8 max-w-lg mx-auto font-light">Échangeons sur votre activité, vos objectifs et le budget adapté.</p>
          <Link to="/devis" data-testid="services-cta" className="btn-gold px-7 py-3.5 inline-flex items-center gap-2 text-sm">
            Demander un devis gratuit <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
